/**
 * SSO 客户端工具 - 供各业务端（admin、user、h5）使用
 */

import { exchangeToken, type ExchangeTokenResult } from "./api/auth.js";
import { RequestError, showBlockingError } from "./request.js";

export interface SSOClientConfig {
  /** 统一登录页地址 */
  authUrl: string;
  /** 当前应用标识 */
  app: string;
  /** 获取当前 token 的函数 */
  getToken: () => string | null;
  /** 保存认证信息的函数（token、uid） */
  saveAuth: (result: ExchangeTokenResult) => void;
  /** 清除认证信息的函数 */
  clearAuth: () => void;
  /** 保存租户代码的函数（在 exchangeToken 之前调用，确保请求头正确） */
  saveTenantCode?: (tenantCode: string) => void;
  /** 获取当前页面完整 URL 的函数（可选，默认使用 window.location.href） */
  getCurrentUrl?: () => string;
}

let ssoConfig: SSOClientConfig | null = null;

// 防止 React StrictMode 下重复调用，使用 Promise 让后续调用等待
let callbackPromise: Promise<"handled" | "failed"> | null = null;

function getErrorMessage(error: unknown): string {
  if (error instanceof RequestError) {
    return (
      (error.data as { message?: string; msg?: string } | undefined)?.message ||
      (error.data as { message?: string; msg?: string } | undefined)?.msg ||
      error.message
    );
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "登入失敗，請重新嘗試";
}

/**
 * 初始化 SSO 客户端配置
 * 应在应用启动时调用
 */
export function initSSOClient(config: SSOClientConfig): void {
  ssoConfig = config;
}

/**
 * 获取 SSO 配置
 */
export function getSSOConfig(): SSOClientConfig | null {
  return ssoConfig;
}

/**
 * 检查是否已登录，未登录则跳转到统一登录页
 * @returns true 表示已登录，false 表示需要跳转（将自动跳转）
 */
export function checkAuthOrRedirect(): boolean {
  if (!ssoConfig) {
    console.warn("[SSO] SSO client not initialized");
    return false;
  }

  const token = ssoConfig.getToken();
  if (token) {
    return true;
  }

  // 构建回调 URL
  const currentUrl = ssoConfig.getCurrentUrl?.() || window.location.href;
  const callbackUrl = new URL(currentUrl);

  // 清理已有的 SSO 相关参数，避免循环嵌套
  callbackUrl.searchParams.delete("code");
  callbackUrl.searchParams.delete("app");
  callbackUrl.searchParams.delete("provider");
  callbackUrl.searchParams.delete("state");
  callbackUrl.searchParams.delete("tenantCode");
  callbackUrl.searchParams.delete("callback");

  // 添加当前应用标识
  callbackUrl.searchParams.set("app", ssoConfig.app);

  // 跳转到统一登录页
  const authUrl = new URL(ssoConfig.authUrl);
  authUrl.searchParams.set("callback", callbackUrl.toString());

  window.location.href = authUrl.toString();
  return false;
}

/**
 * 处理 SSO 回调，从 URL 中获取 code 和 tenantCode 并换取 token
 * @returns Promise<"handled" | "failed" | "not_found">
 */
export async function handleSSOCallback(): Promise<"handled" | "failed" | "not_found"> {
  if (!ssoConfig) {
    console.warn("[SSO] SSO client not initialized");
    return "not_found";
  }

  // 如果已经有正在处理的 Promise，等待它完成并返回结果
  if (callbackPromise) {
    return callbackPromise;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get("code");
  const tenantCode = urlParams.get("tenantCode");
  const provider = urlParams.get("provider") || undefined;
  const app = urlParams.get("app") || ssoConfig.app;

  if (!code) {
    return "not_found";
  }

  // 创建处理 Promise 并保存引用
  callbackPromise = (async () => {
    try {
      // 先保存 tenantCode，确保 exchangeToken 请求头正确
      if (tenantCode && ssoConfig!.saveTenantCode) {
        ssoConfig!.saveTenantCode(tenantCode);
      }

      // 用 code 换取 token
      const exchangeParams = {
        code,
        app,
        ...(provider ? { provider } : {}),
      };
      const result = await exchangeToken(exchangeParams);

      // 保存认证信息
      ssoConfig!.saveAuth(result);

      // 清理 URL 中的 code、provider、state、app、tenantCode 参数
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete("code");
      cleanUrl.searchParams.delete("provider");
      cleanUrl.searchParams.delete("state");
      cleanUrl.searchParams.delete("app");
      cleanUrl.searchParams.delete("tenantCode");
      window.history.replaceState({}, "", cleanUrl.toString());

      return "handled";
    } catch (error) {
      console.error("[SSO] Failed to exchange token:", error);
      await showBlockingError({
        title: "登入失敗",
        content: getErrorMessage(error),
        onOk: async () => {
          ssoConfig!.clearAuth();
          checkAuthOrRedirect();
        },
      });
      return "failed";
    } finally {
      // 重置 Promise 引用
      callbackPromise = null;
    }
  })();

  return callbackPromise;
}

/**
 * 完整的 SSO 认证流程
 * 1. 先尝试处理 SSO 回调（如果 URL 中有 code）
 * 2. 如果没有 code，检查是否已登录
 * 3. 未登录则跳转到统一登录页
 *
 * @returns Promise<boolean> true 表示已认证，false 表示需要跳转（将自动跳转）
 */
export async function ensureAuthenticated(): Promise<boolean> {
  // 先尝试处理回调
  const callbackStatus = await handleSSOCallback();
  if (callbackStatus === "handled") {
    return true;
  }
  if (callbackStatus === "failed") {
    return false;
  }

  // 检查是否已登录
  return checkAuthOrRedirect();
}

/**
 * 登出并跳转到统一登录页
 */
export function ssoLogout(): void {
  if (!ssoConfig) {
    console.warn("[SSO] SSO client not initialized");
    return;
  }

  // 清除认证信息
  ssoConfig.clearAuth();

  // 跳转到统一登录页
  checkAuthOrRedirect();
}
